/**
 * STEP-X SHOPIFY THEME JAVASCRIPT
 * Handles AJAX Cart Drawer, Quick View Modal, Filtering, and Header Interactions
 */

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileNav();
  initCartDrawer();
  initCollectionFilters();
  initQuickView();
  initProductVariantPickers();
});

/* ==========================================================================
   HEADER & SCROLL
   ========================================================================== */
function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  window.addEventListener('scroll', () => {
    if (window.scrollY > 30) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  }, { passive: true });
}

/* ==========================================================================
   MOBILE NAVIGATION DRAWER
   ========================================================================== */
function initMobileNav() {
  const toggleBtn = document.querySelector('.mobile-menu-toggle');
  const drawer = document.querySelector('.mobile-nav-drawer');
  const closeBtn = document.querySelector('.mobile-nav-close');
  if (!toggleBtn || !drawer) return;

  const openNav = () => drawer.classList.add('active');
  const closeNav = () => drawer.classList.remove('active');

  toggleBtn.addEventListener('click', openNav);
  if (closeBtn) closeBtn.addEventListener('click', closeNav);
  drawer.addEventListener('click', (e) => {
    if (e.target === drawer) closeNav();
  });
}

/* ==========================================================================
   AJAX CART DRAWER
   ========================================================================== */
function initCartDrawer() {
  const drawerOverlay = document.querySelector('.cart-drawer-overlay');
  const openButtons = document.querySelectorAll('[data-cart-drawer-open]');
  const closeButtons = document.querySelectorAll('[data-cart-drawer-close]');

  const openDrawer = () => {
    if (drawerOverlay) {
      drawerOverlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      refreshCartUI();
    }
  };

  const closeDrawer = () => {
    if (drawerOverlay) {
      drawerOverlay.classList.remove('active');
      document.body.style.overflow = '';
    }
  };

  openButtons.forEach(btn => btn.addEventListener('click', (e) => {
    e.preventDefault();
    openDrawer();
  }));

  closeButtons.forEach(btn => btn.addEventListener('click', (e) => {
    e.preventDefault();
    closeDrawer();
  }));

  if (drawerOverlay) {
    drawerOverlay.addEventListener('click', (e) => {
      if (e.target === drawerOverlay) closeDrawer();
    });
  }

  // Intercept standard Add-to-Cart form submissions
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.innerText : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerText = 'ADDING...';
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        await refreshCartUI();
        openDrawer();
      } else {
        const err = await res.json();
        alert(err.description || 'Could not add item to cart');
      }
    } catch (err) {
      console.error('Cart add error:', err);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerText = originalText;
      }
    }
  });

  window.openStepXCart = openDrawer;
  window.refreshStepXCart = refreshCartUI;
}

// Format Shopify money amounts
function formatMoney(cents) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents);
  }
  return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

async function refreshCartUI() {
  const itemsContainer = document.querySelector('.cart-drawer-items');
  const subtotalEl = document.querySelector('[data-cart-subtotal]');
  const countBadges = document.querySelectorAll('[data-cart-count]');
  const checkoutBtn = document.querySelector('[data-cart-checkout-btn]');

  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();

    // Update count badges
    countBadges.forEach(badge => {
      badge.textContent = cart.item_count;
      badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    if (!itemsContainer) return;

    if (cart.items.length === 0) {
      itemsContainer.innerHTML = `
        <div style="text-align: center; padding: 4rem 1rem;">
          <p style="font-family: var(--font-display); font-size: 1.25rem; font-weight: 700; margin-bottom: 0.5rem;">Your cart is empty</p>
          <p style="font-size: 0.85rem; color: var(--fg-muted); margin-bottom: 2rem;">Add something worth moving in.</p>
          <button class="btn btn-solid" onclick="document.querySelector('.cart-drawer-overlay').classList.remove('active'); document.body.style.overflow='';">START SHOPPING</button>
        </div>
      `;
      if (subtotalEl) subtotalEl.textContent = formatMoney(0);
      if (checkoutBtn) checkoutBtn.setAttribute('disabled', 'disabled');
      return;
    }

    if (checkoutBtn) checkoutBtn.removeAttribute('disabled');

    itemsContainer.innerHTML = cart.items.map((item, index) => `
      <div class="cart-item" data-key="${item.key}">
        <img src="${item.image ? item.image : ''}" alt="${item.title}" class="cart-item-img">
        <div style="display: flex; flex-direction: column; gap: 0.25rem;">
          <h4 style="font-size: 0.9rem; font-weight: 700;">${item.product_title}</h4>
          ${item.variant_title ? `<p style="font-size: 0.75rem; color: var(--fg-muted); letter-spacing: 0.1em; text-transform: uppercase;">${item.variant_title}</p>` : ''}
          <p style="font-size: 0.85rem; font-weight: 600; margin-top: 0.25rem;">${formatMoney(item.final_line_price)}</p>
        </div>
        <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 0.5rem;">
          <button class="cart-item-remove" onclick="updateCartItemQty('${item.key}', 0)" title="Remove item" style="color: var(--fg-muted); font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em;">REMOVE</button>
          <div class="cart-qty-control">
            <button class="cart-qty-btn" onclick="updateCartItemQty('${item.key}', ${item.quantity - 1})">−</button>
            <span class="cart-qty-num">${item.quantity}</span>
            <button class="cart-qty-btn" onclick="updateCartItemQty('${item.key}', ${item.quantity + 1})">+</button>
          </div>
        </div>
      </div>
    `).join('');

    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }
  } catch (err) {
    console.error('Error fetching cart:', err);
  }
}

window.updateCartItemQty = async function(key, qty) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
    });
    if (res.ok) {
      await refreshCartUI();
    }
  } catch (err) {
    console.error('Error updating cart qty:', err);
  }
};

/* ==========================================================================
   COLLECTION FILTER PILLS
   ========================================================================== */
function initCollectionFilters() {
  const filterButtons = document.querySelectorAll('[data-filter-category]');
  const cards = document.querySelectorAll('.product-card[data-category]');
  if (!filterButtons.length || !cards.length) return;

  filterButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      filterButtons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      const target = btn.getAttribute('data-filter-category').toLowerCase();
      cards.forEach(card => {
        const cardCat = (card.getAttribute('data-category') || '').toLowerCase();
        if (target === 'all' || cardCat === target || cardCat.includes(target)) {
          card.style.display = 'flex';
        } else {
          card.style.display = 'none';
        }
      });
    });
  });
}

/* ==========================================================================
   QUICK VIEW MODAL
   ========================================================================== */
function initQuickView() {
  const modalOverlay = document.querySelector('.quick-view-overlay');
  const closeBtn = document.querySelector('.quick-view-close');
  const triggers = document.querySelectorAll('[data-quick-view-handle]');
  if (!modalOverlay) return;

  const closeModal = () => {
    modalOverlay.classList.remove('active');
    document.body.style.overflow = '';
  };

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) closeModal();
  });

  triggers.forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const handle = btn.getAttribute('data-quick-view-handle');
      if (!handle) return;

      modalOverlay.classList.add('active');
      document.body.style.overflow = 'hidden';

      const contentBox = modalOverlay.querySelector('.quick-view-body');
      if (contentBox) {
        contentBox.innerHTML = '<div style="text-align: center; padding: 4rem;">LOADING DETAILS...</div>';
      }

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error('Product not found');
        const prod = await res.json();
        renderQuickViewModal(prod, contentBox);
      } catch (err) {
        if (contentBox) {
          contentBox.innerHTML = '<div style="text-align: center; padding: 2rem;">Could not load product preview.</div>';
        }
      }
    });
  });
}

function renderQuickViewModal(prod, container) {
  if (!container) return;

  const currentVariant = prod.variants[0] || {};
  const imagesHtml = prod.images.slice(0, 3).map((img, i) => `
    <img src="${img}" alt="${prod.title}" style="width: 100%; height: auto; object-fit: cover; ${i > 0 ? 'margin-top: 1rem;' : ''}">
  `).join('');

  const variantOptionsHtml = prod.variants.map((v, i) => `
    <label style="display: inline-flex; align-items: center; padding: 0.5rem 0.85rem; border: 1px solid var(--border); cursor: pointer; font-size: 0.75rem; text-transform: uppercase;">
      <input type="radio" name="id" value="${v.id}" ${i === 0 ? 'checked' : ''} style="display: none;" onchange="this.closest('form').querySelector('[data-qv-price]').textContent = '${formatMoney(v.price)}'">
      <span>${v.title}</span>
    </label>
  `).join('');

  container.innerHTML = `
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; align-items: start;">
      <div>${imagesHtml}</div>
      <div>
        <p class="tracking-label" style="color: var(--fg-muted); margin-bottom: 0.5rem;">STEP-X DROP</p>
        <h2 style="font-family: var(--font-display); font-size: 1.75rem; font-weight: 800; text-transform: uppercase; margin-bottom: 0.5rem;">${prod.title}</h2>
        <p data-qv-price style="font-size: 1.25rem; font-weight: 700; margin-bottom: 1.5rem;">${formatMoney(currentVariant.price || prod.price)}</p>
        
        <form action="/cart/add" method="post" enctype="multipart/form-data">
          <div style="margin-bottom: 1.5rem;">
            <p class="tracking-label" style="margin-bottom: 0.75rem;">SELECT VARIANT / SIZE</p>
            <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
              ${variantOptionsHtml}
            </div>
          </div>

          <button type="submit" class="btn btn-solid btn-full" style="height: 3.25rem;">ADD TO BAG</button>
          <a href="/products/${prod.handle}" style="display: block; text-align: center; margin-top: 1rem; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.15em;" class="link-underline">VIEW FULL DETAILS →</a>
        </form>
      </div>
    </div>
  `;
}

/* ==========================================================================
   PRODUCT VARIANT SELECTORS
   ========================================================================== */
function initProductVariantPickers() {
  const form = document.querySelector('.product-single-form');
  if (!form) return;

  const variantSelect = form.querySelector('[name="id"]');
  const priceDisplay = document.querySelector('[data-product-price]');
  const mainImage = document.querySelector('[data-product-main-image]');

  // Variant change handler
  if (variantSelect) {
    variantSelect.addEventListener('change', () => {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      const price = selectedOption.getAttribute('data-price');
      const img = selectedOption.getAttribute('data-image');
      if (price && priceDisplay) priceDisplay.textContent = price;
      if (img && mainImage) mainImage.src = img;
    });
  }

  // Thumbnail switcher
  const thumbs = document.querySelectorAll('[data-gallery-thumb]');
  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      const fullUrl = thumb.getAttribute('data-gallery-thumb');
      if (mainImage && fullUrl) {
        mainImage.src = fullUrl;
      }
      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });
}
